/* eslint-disable no-console */
import './utils/polyfills/array-includes';
import './utils/polyfills/object-keys';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { SkeletonTheme } from 'react-loading-skeleton';
import { Provider } from 'react-redux';
import { StatsigProvider } from 'statsig-react';
import registerServiceWorker from './registerServiceWorker';
import { BUILD } from './utils/config';
import { addGTMBody, addGTMHead, clervertapNotificationAfterTimeout } from './utils/library';
import { STATSIG_ENVIRONMENTS } from './utils/constants';
import { store } from './store';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import 'semantic-ui-css/semantic.min.css';
import 'react-datepicker/dist/react-datepicker.css';
import './utils/styles/main.scss';

import App from './components/app/App';
import Theme from './Theme';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import COLORS from './common/styles/color-pallete';
import ChatBotWrapper from './screens/help-center/wrappers/ChatBotWrapper';
import { statsigIdentifer } from './constants/evs/statsigClientKey';
import SEOMetaTags from './components/seo/SEOMetaTags';
import { zendeskApiKey } from './constants/evs/zendeskKey';

// Load Zendesk script dynamically
const zendeskKey = zendeskApiKey;
if (zendeskKey) {
  const script = document.createElement('script');
  script.id = 'ze-snippet';
  script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
  script.defer = true;
  document.body.appendChild(script);
}

const queryClient = new QueryClient();

const statsigOptions = () => {
  return {
    environment: STATSIG_ENVIRONMENTS.PRODUCTION
  };
};

// Root instance is memoized so HMR re-renders reuse it instead of double-mounting.
let root = null;

const initializeGTM = () => {
  addGTMHead();
  addGTMBody();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.buildType = BUILD;
};

const initializeCleverTap = () => {
  if (window.clevertap) {
    clervertapNotificationAfterTimeout();
  }
};

const getRoot = () => {
  if (!root) {
    const container = document.getElementById('app');
    root = createRoot(container);
  }
  return root;
};

const createAppTree = (Component) => (
  <StatsigProvider sdkKey={statsigIdentifer} waitForInitialization={true} options={statsigOptions}>
    <ThemeProvider theme={Theme}>
      <BrowserRouter useTransitions={false}>
        <Provider store={store}>
          <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
              <SkeletonTheme baseColor={COLORS.black} highlightColor={COLORS.grey200}>
                <ChatBotWrapper />
                <SEOMetaTags />
                <Component />
              </SkeletonTheme>
            </ErrorBoundary>
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
          </QueryClientProvider>
        </Provider>
      </BrowserRouter>
    </ThemeProvider>
  </StatsigProvider>
);

const renderApp = (Component) => {
  const rootInstance = getRoot();
  rootInstance.render(createAppTree(Component));
};

function main(Component) {
  renderApp(Component);
  initializeGTM();
}

async function _main(Component) {
  initializeCleverTap();
  initializeGTM();

  await new Promise((resolve) => {
    renderApp(Component);
    resolve();
  });

  await registerServiceWorker();

  return 'DONE_RENDERING';
}

if (import.meta.hot) {
  import.meta.hot.accept('./components/app/App', (newModule) => {
    if (newModule) {
      renderApp(newModule.default);
    }
  });
}

if (BUILD === 'prod') {
  _main(App).then(console.log).catch(console.error);
} else {
  main(App);
}
